import React, { useState, useCallback, useMemo } from 'react';
import { FieldInputProps, FormikProps } from 'formik';
import { FieldConfig } from '../../../../services/ValidationService';
import { DECIMAL_TEXT_INPUT_ATTRIBUTES, isCommittable, isPendingRawText, resolveDecimalFieldPolicy } from '../../../../utils/decimalInput';
import { useDecimalDraft } from '../../../../hooks/useDecimalDraft';

interface FormattedInputProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field: FieldInputProps<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: FormikProps<any>;
    fieldConfig: FieldConfig;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: any) => void, fieldName: string) => void;
    nestedFieldName: string;
    showSymbol: boolean;
    isDisabled: boolean;
    isFieldReadOnly: boolean;
    unitArray?: string | string[];
    projectUnitSystem?: 'Metric' | 'Imperial';
}

const FormattedInput: React.FC<FormattedInputProps> = ({
    field,
    form,
    fieldConfig,
    handleInputChange,
    nestedFieldName,
    showSymbol,
    isDisabled,
    isFieldReadOnly,
    unitArray,
    projectUnitSystem = 'Metric',
}) => {
    const [isFocused, setIsFocused] = useState(false);

    // Calculate unit to display based on unitArray and projectUnitSystem
    const displayUnit = useMemo(() => {
        if (!unitArray) return '';
        if (typeof unitArray === 'string') return unitArray;

        // Dynamic unit based on Metric/Imperial
        const index = projectUnitSystem === 'Metric' ? 0 : 1;
        return unitArray[index] || '';
    }, [unitArray, projectUnitSystem]);

    // Helper function to format display value with European locale (dot as thousand separator)
    const formatDisplayValue = useCallback(
        (value: unknown): string => {
            if (value === null || value === undefined || value === '') return '';
            const numValue = Number(value);
            if (isNaN(numValue)) return String(value);

            // For integers or large numbers, use European formatting with dot as thousand separator
            if (fieldConfig.format === 'integer') {
                return Math.round(numValue).toLocaleString('de-DE');
            }

            // For large numbers (>= 1000), apply European thousand separator formatting
            if (Math.abs(numValue) >= 1000) {
                if (fieldConfig.decimalPlaces !== undefined) {
                    return numValue.toLocaleString('de-DE', {
                        minimumFractionDigits: fieldConfig.decimalPlaces,
                        maximumFractionDigits: fieldConfig.decimalPlaces,
                    });
                }
                // For integers displayed as numbers, no decimal places
                if (Number.isInteger(numValue)) {
                    return numValue.toLocaleString('de-DE', { maximumFractionDigits: 0 });
                }
                return numValue.toLocaleString('de-DE', { maximumFractionDigits: 2 });
            }

            // For smaller numbers, keep standard formatting
            if (fieldConfig.format === 'float' && fieldConfig.decimalPlaces !== undefined) {
                return numValue.toFixed(fieldConfig.decimalPlaces);
            }
            return String(value);
        },
        [fieldConfig.format, fieldConfig.decimalPlaces]
    );

    const isNumberField = fieldConfig.type === 'number';
    const decimalPolicy = useMemo(() => resolveDecimalFieldPolicy(fieldConfig, 'dynamic'), [fieldConfig]);

    const notifyInputChange = (value: string) => {
        if (!handleInputChange) return;
        const normalizedEvent = { target: { value, name: field.name } } as React.ChangeEvent<HTMLInputElement>;
        handleInputChange(normalizedEvent, form.setFieldValue, fieldConfig.name);
    };

    // Unusable text goes to Formik as typed, so Yup blocks RUN and save; only usable values reach handleInputChange
    const decimalDraft = useDecimalDraft({
        value: field.value,
        policy: decimalPolicy,
        emptyPolicy: 'publishEmpty',
        formatBlurred: formatDisplayValue,
        resetKey: isPendingRawText(field.value, decimalPolicy) ? 'raw' : 'value',
        onPublish: event => {
            if (event.kind === 'value' || event.kind === 'empty') {
                const text = event.kind === 'value' ? event.text : '';
                form.setFieldValue(nestedFieldName, text);
                notifyInputChange(text);
            } else {
                form.setFieldValue(nestedFieldName, event.raw);
            }
        },
    });

    const displayValue: string = isFocused ? String(field.value ?? '') : formatDisplayValue(field.value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue(nestedFieldName, e.target.value);
        if (handleInputChange) {
            handleInputChange(e, form.setFieldValue, fieldConfig.name);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        if (isNumberField) {
            decimalDraft.inputProps.onBlur();
            // A usable number keeps today's rounding on blur; drafts were already committed by the draft hook
            if (isCommittable(field.value, decimalPolicy)) {
                const decimalPlaces = fieldConfig.decimalPlaces !== undefined ? fieldConfig.decimalPlaces : 2;
                const cleanedValue = parseFloat(Number(field.value).toFixed(decimalPlaces)).toString();
                form.setFieldValue(nestedFieldName, cleanedValue);
            }
        }

        field.onBlur(e);
    };

    const numberInputProps = isNumberField
        ? { ...DECIMAL_TEXT_INPUT_ATTRIBUTES, value: decimalDraft.inputProps.value, onChange: decimalDraft.inputProps.onChange, onKeyDown: decimalDraft.inputProps.onKeyDown }
        : { type: 'text', value: String(displayValue), onChange: handleChange };

    return (
        <div className="input-container">
            <input
                {...field}
                {...numberInputProps}
                id={nestedFieldName}
                onFocus={() => {
                    setIsFocused(true);
                    if (isNumberField) decimalDraft.inputProps.onFocus();
                }}
                onBlur={handleBlur}
                className={`${showSymbol ? 'input-with-symbol' : ''} ${displayUnit ? 'input-with-unit' : ''}`.trim()}
                disabled={isDisabled || isFieldReadOnly}
                readOnly={isFieldReadOnly}
                style={isFieldReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            />
            {displayUnit && <span className="input-unit-inside">{displayUnit}</span>}
        </div>
    );
};

export default FormattedInput;
